import { maybe, rd } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { ArrowRightLeft } from 'lucide-react';
import { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import { renderError } from 'v5/patterns/widgets/renderError';
import { Button, buttonVariants } from 'v5/design-system/Button';
import {
  DialogDescription,
  DialogHeader,
  DialogTitle
} from 'v5/design-system/Dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from 'v5/design-system/Form';
import { Skeleton } from 'v5/design-system/Skeleton';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { EnterpriseWorkspacePicker } from 'v5/features/core/enterprise/EnterpriseWorkspacePicker';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { DialogErrorFooter } from 'v5/patterns/forms/dialogs/DialogErrorFooter';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { cn } from 'v5/platform/dom/cn';
import { getUserFacingErrorMessage } from 'v5/platform/lang/getUserFacingErrorMessage';
import { WithServices } from 'v5/platform/ts/services';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';

export type TransferToWorkspaceDialogContentProps = WithServices<
  [WithEnterpriseService]
> & {
  open: boolean;
  organizationId: string;
  currentWorkspaceId?: number | null;
  onSubmit: (workspace: EnterpriseWorkspace) => Promise<void>;
  titleMessage: ReactNode;
  descriptionMessage: string;
  fieldWorkspaceMessage: ReactNode;
};

type TransferToWorkspaceFormData = {
  workspaceId: string | null;
  selectedWorkspace: EnterpriseWorkspace | null;
};

function WorkspaceTriggerContent({
  workspace,
  placeholder
}: {
  workspace: EnterpriseWorkspace | null;
  placeholder: string;
}) {
  if (workspace == null) {
    return <span className="text-fg-muted truncate">{placeholder}</span>;
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      <EnterpriseWorkspaceIcon
        name={workspace.name}
        iconUrl={workspace.iconUrl}
      />
      <span className="truncate">{workspace.name}</span>
    </div>
  );
}

function WorkspaceDisplayShell({ children }: { children: ReactNode }) {
  return (
    <div
      className={cn(
        buttonVariants({
          variant: 'secondary',
          size: 'sm',
          className: 'w-full justify-start font-normal'
        })
      )}
      aria-readonly
    >
      {children}
    </div>
  );
}

function CurrentWorkspaceDisplay({
  organizationId,
  workspaceId,
  label,
  services
}: {
  organizationId: string;
  workspaceId: number;
  label: string;
  services: TransferToWorkspaceDialogContentProps['services'];
}) {
  const workspaceRd = services.enterpriseService.useWorkspace(
    organizationId,
    workspaceId
  );

  return (
    <FormItem>
      <FormLabel>{label}</FormLabel>
      <WorkspaceDisplayShell>
        {rd
          .journey(workspaceRd)
          .wait(<Skeleton className="h-4 w-40" />)
          .catch(renderError)
          .map(workspace =>
            maybe.isPresent(workspace) ? (
              <WorkspaceTriggerContent workspace={workspace} placeholder="—" />
            ) : (
              <span className="text-fg-muted">—</span>
            )
          )}
      </WorkspaceDisplayShell>
    </FormItem>
  );
}

export function TransferToWorkspaceDialogContent({
  open,
  organizationId,
  currentWorkspaceId = null,
  onSubmit,
  services,
  titleMessage,
  descriptionMessage,
  fieldWorkspaceMessage
}: TransferToWorkspaceDialogContentProps) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'TransferToWorkspaceDialog'
  });
  const mutation = promiseState.useMutation(
    async (workspace: EnterpriseWorkspace) => {
      await onSubmit(workspace);
    }
  );

  const form = useForm<TransferToWorkspaceFormData>({
    defaultValues: { workspaceId: null, selectedWorkspace: null }
  });

  useRegisterDirtyState(form.formState.isDirty);

  const workspaceId = form.watch('workspaceId');
  const isSameWorkspace =
    workspaceId != null &&
    currentWorkspaceId != null &&
    Number(workspaceId) === currentWorkspaceId;

  return (
    <Form {...form}>
      <FormDialogContent
        open={open}
        className="max-w-120"
        title={titleMessage}
        onSubmit={form.handleSubmit(data => {
          if (data.selectedWorkspace == null) {
            return;
          }
          mutation.track(data.selectedWorkspace);
        })}
        mutation={mutation.state}
        saveText={t('save')}
        disabled={!workspaceId || isSameWorkspace}
        footerContent={
          <DialogErrorFooter
            compact
            mutation={mutation.state}
            renderError={error =>
              getUserFacingErrorMessage(error) || t('transferFailed')
            }
          />
        }
        saveButtonSlot={
          <AsyncButton
            type="submit"
            mutation={mutation.state}
            variant="primary"
            className="shrink-0 self-center"
            visuallyDisabled={!workspaceId || isSameWorkspace}
            leftIcon={<ArrowRightLeft className="size-4" />}
          >
            {t('save')}
          </AsyncButton>
        }
        renderHeader={title => (
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{descriptionMessage}</DialogDescription>
          </DialogHeader>
        )}
      >
        {currentWorkspaceId != null ? (
          <CurrentWorkspaceDisplay
            organizationId={organizationId}
            workspaceId={currentWorkspaceId}
            label={t('fieldCurrentWorkspace')}
            services={services}
          />
        ) : null}
        <FormField
          control={form.control}
          name="workspaceId"
          rules={{
            required: true,
            validate: value =>
              value != null &&
              (currentWorkspaceId == null ||
                Number(value) !== currentWorkspaceId)
                ? true
                : t('sameWorkspace')
          }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{fieldWorkspaceMessage}</FormLabel>
              <FormControl>
                <EnterpriseWorkspacePicker
                  organizationId={organizationId}
                  services={services}
                  disabledWorkspaceId={currentWorkspaceId}
                  onWorkspaceSelect={workspace => {
                    field.onChange(String(workspace.id));
                    form.setValue('selectedWorkspace', workspace, {
                      shouldDirty: true,
                      shouldValidate: true
                    });
                  }}
                >
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="w-full justify-start font-normal"
                  >
                    <WorkspaceTriggerContent
                      workspace={form.watch('selectedWorkspace')}
                      placeholder={t('selectWorkspace')}
                    />
                  </Button>
                </EnterpriseWorkspacePicker>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </FormDialogContent>
    </Form>
  );
}
